import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './text-diff.component.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { PersistedInput } from '../../../utils/persisted-input';

const STORAGE_KEY = 'text-diff';
const isValidPersisted = (v: unknown): v is { original: string; modified: string } =>
    !!v && typeof v === 'object'
    && typeof (v as any).original === 'string'
    && typeof (v as any).modified === 'string';

interface DiffState {
    original: string;
    modified: string;
    copiedField: string | null;
}

type DiffLine = { type: 'added' | 'removed' | 'unchanged'; text: string };

function lcs(a: string[], b: string[]): boolean[][] {
    const m = a.length, n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++)
        for (let j = 1; j <= n; j++)
            dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);

    const inLcs: boolean[][] = [Array(m).fill(false), Array(n).fill(false)];
    let i = m, j = n;
    while (i > 0 && j > 0) {
        if (a[i - 1] === b[j - 1]) { inLcs[0][i - 1] = true; inLcs[1][j - 1] = true; i--; j--; }
        else if (dp[i - 1][j] > dp[i][j - 1]) i--;
        else j--;
    }
    return inLcs;
}

function computeDiff(original: string, modified: string): DiffLine[] {
    const a = original.split('\n'), b = modified.split('\n');
    const [inA, inB] = lcs(a, b);
    const result: DiffLine[] = [];
    let ai = 0, bi = 0;
    while (ai < a.length || bi < b.length) {
        if (ai < a.length && inA[ai] && bi < b.length && inB[bi]) {
            result.push({ type: 'unchanged', text: a[ai] }); ai++; bi++;
        } else {
            if (ai < a.length && !inA[ai]) { result.push({ type: 'removed', text: a[ai] }); ai++; }
            else if (bi < b.length && !inB[bi]) { result.push({ type: 'added', text: b[bi] }); bi++; }
        }
    }
    return result;
}

export default class TextDiffComponent extends Component<{ feature?: string }, DiffState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    state: DiffState = (() => {
        const restored = PersistedInput.load(STORAGE_KEY, { original: '', modified: '' }, isValidPersisted);
        return { original: restored.original, modified: restored.modified, copiedField: null };
    })();

    componentDidUpdate(_: any, prev: DiffState) {
        if (prev.original !== this.state.original || prev.modified !== this.state.modified) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                PersistedInput.save(STORAGE_KEY, { original: this.state.original, modified: this.state.modified });
            }, 500);
        }
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    copyDiff = (diff: DiffLine[]) => {
        const text = diff.map(d => {
            const prefix = d.type === 'added' ? '+ ' : d.type === 'removed' ? '- ' : '  ';
            return prefix + d.text;
        }).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'diff' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    render() {
        const { original, modified, copiedField } = this.state;
        const diff = (original || modified) ? computeDiff(original, modified) : [];
        const added = diff.filter(d => d.type === 'added').length;
        const removed = diff.filter(d => d.type === 'removed').length;
        const unchanged = diff.filter(d => d.type === 'unchanged').length;

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={styles.sideBySide}>
                        <div>
                            <label className={sharedStyles.label}>Original</label>
                            <textarea
                                className={sharedStyles.textarea}
                                placeholder="Paste original text..."
                                value={original}
                                onChange={e => this.setState({ original: e.target.value })}
                                rows={8}
                            />
                        </div>
                        <div>
                            <label className={sharedStyles.label}>Modified</label>
                            <textarea
                                className={sharedStyles.textarea}
                                placeholder="Paste modified text..."
                                value={modified}
                                onChange={e => this.setState({ modified: e.target.value })}
                                rows={8}
                            />
                        </div>
                    </div>

                    {diff.length > 0 && (
                        <>
                            <div className={sharedStyles.stats}>
                                <span>Added:<strong>{added}</strong></span>
                                <span>Removed:<strong>{removed}</strong></span>
                                <span>Unchanged:<strong>{unchanged}</strong></span>
                            </div>

                            <div className={styles.diffOutput}>
                                {(() => {
                                    // Track original-side and modified-side line numbers separately
                                    // so a viewer can map each row back to its source location.
                                    // Removed lines advance only the original counter, added lines
                                    // advance only the modified counter, unchanged advance both.
                                    let origLine = 0;
                                    let modLine = 0;
                                    return diff.map((d, i) => {
                                        if (d.type === 'removed') origLine++;
                                        else if (d.type === 'added') modLine++;
                                        else { origLine++; modLine++; }
                                        const oNum = d.type === 'added' ? '' : origLine;
                                        const mNum = d.type === 'removed' ? '' : modLine;
                                        return (
                                            <div key={i} className={`${styles.diffLine} ${styles[d.type]}`}>
                                                <span className={styles.lineNum} title="original line">{oNum}</span>
                                                <span className={styles.lineNum} title="modified line">{mNum}</span>
                                                {d.type === 'added' ? '+ ' : d.type === 'removed' ? '- ' : '  '}
                                                {d.text}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            <div className={sharedStyles.actions}>
                                <button
                                    className={`${sharedStyles.copyButton} ${copiedField === 'diff' ? sharedStyles.copyButtonSuccess : ''}`}
                                    onClick={() => this.copyDiff(diff)}
                                >
                                    {copiedField === 'diff' ? '✓ Copied' : 'Copy Diff'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            </FullscreenHost>
        );
    }
}
